<template>
  <div>
    <global-dialog-card v-model="dialog_add" :max-width="1000" :title="$t(formTitle)">
      <v-row>
        <v-col cols="4" class="mb-0 pb-0">
          <text-field v-model="addItem.name" :label="$t('*title')"></text-field>
        </v-col>
        <v-col cols="4" class="mb-0 pb-0">
          <autocomplete
            v-model="addItem.country"
            :items="countries || []"
            :label="$t('country')"
            required
            item-text="name"
            item-id="id"
            return-object
          />
        </v-col>
        <v-col cols="4" class="mb-0 pb-0">
          <autocomplete
            v-model="addItem.city_id"
            :items="addItem.country.city || []"
            :label="$t('city') + ' (' + $t('company_headquarters') + ')'"
            item-text="name"
            item-id="id"
            return-object
          />
        </v-col>
        <v-col cols="4" class="mb-0 pb-0">
          <text-field v-model="addItem.address" :label="'* ' + $t('address')">
          </text-field>
        </v-col>
        <v-col cols="4" class="mb-0 pb-0">
          <text-field v-model="addItem.phone" :label="$t('phone')"></text-field>
        </v-col>
        <v-col cols="4" class="mb-0 pb-0">
          <text-field v-model="addItem.email" :label="$t('email')" required> </text-field>
        </v-col>
        <v-col cols="5" class="mb-0 pb-0">
          <v-checkbox
            :label="$t('local')"
            type="number"
            outlined
            v-bind:false-value="0"
            v-bind:true-value="1"
            v-model.number="addItem.local"
          >
          </v-checkbox>
          <span v-if="addItem.local">
            <span class="pt-4 d-block" v-if="!addItem.country">
              {{ $t("choose_country") }}
            </span>
            <span v-else>
              <autocomplete
                v-model="addItem.local_cities"
                :items="addItem.country.city || []"
                :label="$t('select_delivery_cities')"
                item-text="name"
                item-id="id"
                item-value="id"
                multiple
              />
            </span>
          </span>
        </v-col>
        <v-col cols="5" class="mb-0 pb-0">
          <v-checkbox
            class="ml-4"
            :label="$t('intercity')"
            type="number"
            outlined
            v-bind:false-value="0"
            v-bind:true-value="1"
            v-model.number="addItem.intercity"
          >
          </v-checkbox>
          <span v-if="addItem.intercity">
            <span class="pt-4 d-block" v-if="!addItem.country">
              {{ $t("choose_country") }}
            </span>
            <span v-else class="pt-4">
              <v-checkbox
                class="ml-4"
                :label="$t('delivery_entire_country')"
                type="number"
                outlined
                v-bind:false-value="0"
                v-bind:true-value="1"
                v-model.number="addItem.all_cities"
              >
              </v-checkbox>
              <span v-if="!addItem.all_cities">
                <v-row>
                  <v-col cols="8" class="mb-0 pt-0 pb-0">
                    <span v-for="(route, index) in addItem.routes" class="d-flex">
                      <autocomplete
                        v-model="route.city"
                        :items="addItem.country.city || []"
                        :label="$t('route') + ' ' + (index + 1)"
                        item-text="name"
                        item-id="id"
                        item-value="id"
                      />
                      <remove-button
                        v-if="addItem.routes.length > 1"
                        @click="removeFromCities(index)"
                      ></remove-button>
                    </span>
                  </v-col>
                  <v-col cols="4" class="mb-0 pt-3 pb-0">
                    <primary-button @click="addMoreRoutes(addItem.country.city)">
                      <v-icon class="mr-2">mdi-plus-circle</v-icon>
                      {{ $t("route") }}
                    </primary-button>
                  </v-col>
                </v-row>
              </span>
            </span>
          </span>
        </v-col>
      </v-row>
      <template #actions>
        <v-btn text @click="close">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button @click="save">{{ $t("save") }}</primary-button>
      </template>
    </global-dialog-card>

    <page-header
      icon="mdi-truck-delivery"
      :title="$t('delivery_posts')"
      :subtitle="$t('manage_delivery_posts')"
    >
      <template #actions>
        <primary-button @click="dialog_add = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar v-model="search" :search-placeholder="$t('search_posts_placeholder')"/>

    <div class="px-6 mt-2">
      <v-data-table
        :headers="headers"
        :items="posts || []"
        :search="search"
        sort-by="name"
        class="ordera-box-shadow striped-table"
      >
        <template v-slot:top>
          <v-divider class="mt-2"></v-divider>
        </template>
        <template
          v-for="header in headers"
          v-slot:[`header.${header.value}`]="{ header }"
        >
          {{ $t(header.text) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-switch
            @click="switchStatus(item)"
            v-model="item.status"
            :false-value="0"
            :true-value="1"
          ></v-switch>
        </template>
        <template v-slot:item.coverage="{ item }">
          {{ item.coverage == 1 ? $t("local") : $t("intercity") }}
        </template>
        <template v-slot:item.actions="{ item }">
          <edit-button class="mr-2" @click="editPost(item)"></edit-button>
          <remove-button @click="openDeletePost(item)"></remove-button>
        </template>
      </v-data-table>
    </div>
    <global-dialog-card v-model="dialog" fullscreen :title="$t('edit')">
      <v-row>
        <v-col cols="7" class="mb-0 pb-0">
          <v-row>
            <v-col cols="4" class="mb-0 pb-0">
              <text-field
                v-model="editedItem.name"
                :label="$t('title')"
                required
              ></text-field>
            </v-col>
            <v-col cols="4" class="mb-0 pb-0">
              <autocomplete
                v-model="editedItem.country"
                :items="countries || []"
                :label="$t('country')"
                item-text="name"
                item-id="id"
                return-object
              />
            </v-col>
            <v-col cols="4" class="mb-0 pb-0">
              <autocomplete
                v-if="editedItem.country && editedItem.country.city"
                v-model="editedItem.city"
                :items="editedItem.country.city ? editedItem.country.city : []"
                :label="$t('city') + ' (' + $t('company_headquarters') + ')'"
                item-text="name"
                item-id="id"
                return-object
              />
            </v-col>
            <v-col cols="4" class="mb-0 pb-0">
              <text-field v-model="editedItem.address" :label="$t('address')" required>
              </text-field>
            </v-col>
            <v-col cols="4" class="mb-0 pb-0">
              <text-field v-model="editedItem.phone" :label="$t('phone')"></text-field>
            </v-col>
            <v-col cols="4" class="mb-0 pb-0">
              <text-field v-model="editedItem.email" :label="$t('email')" required>
              </text-field>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="5" class="mb-0 pb-0">
          <!-- POKRIVENOST-->
          <v-col cols="12" class="mb-0 pt-0 pb-0">
            <span v-if="editedItem.coverage.val == 1">
              <span v-if="!editedItem.country">
                {{ $t("choose_country") }}
              </span>
              <span v-else>
                <autocomplete
                  v-model="editedItem.city_post"
                  :items="editedItem.country.city || []"
                  :label="$t('select_delivery_cities')"
                  item-text="name"
                  item-id="id"
                  item-value="id"
                  multiple
                  return-object
                />
              </span>
            </span>
            <span v-if="editedItem.coverage.val == 2">
              <span v-if="!editedItem.country">
                {{ $t("choose_country") }}
              </span>
              <span v-else>
                <v-checkbox
                  :label="$t('delivery_entire_country')"
                  type="number"
                  outlined
                  v-bind:false-value="0"
                  v-bind:true-value="1"
                  v-model.number="editedItem.all_cities"
                >
                </v-checkbox>
                <span v-if="editedItem.all_cities != 1">
                  <v-row>
                    <v-col cols="8" class="mb-0 pt-0 pb-0">
                      <span
                        v-for="(route1, index) in editedItem.city_post"
                        class="d-flex"
                      >
                        <autocomplete
                          v-model="route1.city"
                          :items="editedItem.country.city || []"
                          :label="$t('route') + ' ' + (index + 1)"
                          item-text="name"
                          item-id="id"
                          item-value="id"
                          return-object
                        ></autocomplete>
                        <remove-button
                          v-if="editedItem.city_post.length > 1"
                          @click="removeFromCities(index)"
                        ></remove-button>
                      </span>
                    </v-col>
                    <v-col cols="4" class="mb-0 pt-3 pb-0">
                      <primary-button @click="addMoreRoutes(editedItem.country.city)">
                        <v-icon class="mr-2">mdi-plus-circle</v-icon>
                        {{ $t("route") }}
                      </primary-button>
                    </v-col>
                  </v-row>
                </span>
              </span>
            </span>
          </v-col>
        </v-col>
      </v-row>
      <div class="text-right mt-3">
        <v-btn
          outlined
          style="border-radius: 9px"
          color="primary"
          small
          class="mr-2"
          @click="editMessage"
        >
          {{ $t("edit") }}
        </v-btn>
      </div>
    </global-dialog-card>
    <global-dialog-card
      v-model="dialogDelete"
      :max-width="500"
      :title="$t('delete_company')"
    >
      <template #actions>
        <v-btn text @click="closeDelete">{{ $t("cancel") }}</v-btn>
        <primary-button @click="deletePost">{{ $t("confirm") }}</primary-button>
      </template>
    </global-dialog-card>
  </div>
</template>

<script>
import conf from "@/config";
import postService from "@/services/post";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  mixins: [hasSnackbar],
  data() {
    return {
      conf: conf,
      dialog: false,
      dialog_add: false,
      dialogDelete: false,
      poruka: "",
      search: "",
      posts: "",
      restorani: [],
      restoran: "",
      kompanije: [],
      kompanija: "",
      korisnici: [],
      korisnik: [],
      edit_post: "",
      editedItem: {
        name: "",
        coverage: "",
        address: "",
        city_id: "",
        cities: [],
        local: 0,
        intercity: 0,
        local_cities: [],
        country: "",
        phone: "",
        routes: [
          {
            city: "",
          },
        ],
        email: "",
        all_cities: 1,
      },
      addItem: {
        name: "",
        coverage: "",
        address: "",
        city_id: "",
        cities: [],
        local: 0,
        intercity: 0,
        local_cities: [],
        country: "",
        phone: "",
        routes: [
          {
            city: "",
          },
        ],
        email: "",
        all_cities: 1,
      },
      defaultItem: {
        name: "",
        coverage: "",
        address: "",
        city_id: "",
        cities: [],
        country: "",
        phone: "",
        email: "",
        all_cities: 1,
      },
      cities: "",
      countries: "",
      editedIndex: -1,
      headers: [
        { text: "Id", align: "start", value: "id", sortable: true },
        { text: "title", align: "start", value: "name", sortable: true },
        { text: "coverage", align: "start", value: "coverage", sortable: true },
        { text: "address", align: "start", value: "address", sortable: true },
        { text: "phone", align: "start", value: "phone", sortable: true },
        { text: "email", align: "start", value: "email", sortable: true },
        { text: "status", align: "start", value: "status", sortable: true },
        { text: "actions", align: "center", value: "actions", sortable: false },
      ],
      deleteItem: "",
    };
  },

  created() {
    this.load();
    this.getCities();
    this.getCountries();
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("add")
        : this.$t("send_by_mail") + ": " + this.editedItem.id;
    },
  },

  methods: {
    removeFromCities(index) {
      this.editedItem.city_post.splice(index, 1);
    },
    addMoreRoutes(cities) {
      this.addItem.city_post.push({
        city: "",
        cities: cities,
      });
    },
    getCity() {},
    async switchStatus(item) {
      try {
        await postService.editPostStatus(item);
        this.showSuccessSnackbar(this.$t("post_status_updated"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_updating_post_status"));
      }
    },
    openDeletePost(post) {
      this.deleteItem = post;
      this.dialogDelete = true;
    },
    closeDelete() {
      this.dialogDelete = false;
    },
    async getCountries() {
      try {
        const { data } = await postService.getCountries();
        this.countries = data.sort((a, b) => a.name - b.name);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_countries"));
      }
    },
    async getCities() {
      try {
        const { data } = await postService.getAllCities();
        this.cities = data.sort((a, b) => a.name - b.name);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_cities"));
      }
    },
    close() {
      this.dialog_add = false;
    },
    async save() {
      try {
        const { data } = await postService.storePost(this.addItem);
        if (data) {
          this.posts = data;
          this.dialog_add = false;
          this.addItem.name = "";
          this.addItem.coverage = "";
          this.addItem.address = "";
          this.addItem.city_id = "";
          this.addItem.country = "";
          this.addItem.phone = "";
          this.addItem.email = "";
          this.showSuccessSnackbar(this.$t("post_created"));
        }
        this.$forceUpdate();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_creating_post"));
      }
    },
    async load() {
      try {
        const { data } = await postService.getAllPosts();
        data.filter((item) => {
          item.coverage = {};
          let selected_citise = [];
          if (item.city_post[0].local == 1) {
            item.coverage.val = 1;
            item.coverage.name = "Локал";
            item.coverage.id = 1;
          }
          if (item.city_post[0].intercity == 1) {
            item.coverage.val = 2;
            item.coverage.name = "Међуградско";
            item.coverage.id = 2;
          }
          if (item.city_post.length > 0) {
            item.city_post.filter((city) => {
              city.name = city.city.name;
              city.city.id = parseInt(city.city.id);
              selected_citise.push(city.city);
              city.id = city.city.id;
            });
          }
          item.city_id = selected_citise;
        });
        this.posts = data;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_posts"));
      }
    },
    editPost(poruka) {
      this.dialog = true;
      this.editedItem = poruka;
      this.$forceUpdate();
    },
    saveMessage() {
      if (this.poruka != "") {
        postService.saveMessage(this.poruka).then(({ data }) => {
          if (data) {
            this.posts.push(data.data);
            this.poruka = "";
          }
        });
      }
    },
    async editMessage() {
      try {
        const { data } = await postService.editPost(this.editedItem);
        if (data) {
          this.posts.filter((item) => {
            if (item.id == data.id) {
              item = data;
            }
          });
          this.dialog = false;
          this.showSuccessSnackbar(this.$t("post_updated"));
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_updating_post"));
      }
    },
    async deletePost(poruka) {
      try {
        const { data } = await postService.deletePost(this.deleteItem);
        if (data) {
          this.posts.filter((item, index) => {
            if (item.id == this.deleteItem.id) {
              this.posts.splice(index, 1);
            }
          });
          this.$forceUpdate();
          this.dialogDelete = false;
          this.showSuccessSnackbar(this.$t("post_deleted"));
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_deleting_post"));
      }
    },
  },
};
</script>

<style scoped>
label {
  display: inline-block;
  color: white;
  padding: 0.5rem;
  font-family: sans-serif;
  border-radius: 0.3rem;
  cursor: pointer;
  margin-top: 1rem;
}

@media (max-width: 767px) {
  .col-sm-4 {
    max-width: 33.333%;
  }
}
</style>
